/**
 * skyGradient — the lit-sky backplane (the deepest plane, Z_LAYERS.SKY) so a
 * scene's parallax silhouettes have a sky to read against (PX2). Drawn ONCE as a
 * single GPU-interpolated vertical fill. Solid strips remain only as a fallback
 * for lightweight renderers that do not expose gradient fills.
 *
 * SCENE-ANCHORED (scene-schema, T6): the band is FIXED vertically to the scene's
 * sky region — it spans world-Y `ctx.scene.skyTopY → ctx.scene.horizonY` (where
 * the parallax silhouettes sit) and never moves up/down with the camera. Only the
 * horizontal position tracks the camera (overscanned so a pan never reveals an
 * edge). This makes the sky meet the horizon line the same way the terrain does.
 * Best-effort: no camera yet → a quieter scene, never a throw.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { createGraphics } from '../../../rendering/utils/Graphics';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { gradientColors } from './skyGradientConfig';

export const SKY_ZENITH_CAP_H = 3000;
export const SKY_NADIR_SKIRT_H = 1500;

const buildSkyGradient: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'skyGradient') return null;

  const gfx = createGraphics(null, { depth: Z_LAYERS.SKY, parent: ctx.layerContainer });
  const colors = gradientColors(layer.from, layer.to, layer.bands ?? 24);

  // VERTICAL: the scene's sky band (world-Y, fixed). The ramp runs top→bottom from
  // skyTopY (`from`) down to horizonY (`to`), exactly filling the band height. This
  // is scene-based (always available); only the horizontal width needs the camera.
  const spanH = Math.max(0, ctx.scene.horizonY - ctx.scene.skyTopY);
  const strip = spanH / colors.length;

  // Each frame: track the camera HORIZONTALLY (centred on centerX), but pin the top
  // of the band to the scene's skyTopY (world-Y) so the sky stays welded to the
  // horizon regardless of vertical camera motion. Do NOT gate the BUILD on the
  // camera — it may bind AFTER the atmosphere mount on a slow boot (LEVEL_LOADED
  // race), which would silently drop the sky backplane. Draw the gradient on the
  // FIRST tick the camera is available, and redraw on a width change (resize).
  // The ramp is opaque — per-layer opacity rides the container (EffectLayerBase.opacity).
  let drawnW = 0;
  const fit = () => {
    const v = ctx.getCameraView();
    if (!v) return; // camera not bound yet — try again next tick
    const W = v.width * 1.2; // overscan 20% so a horizontal pan never reveals an edge
    if (W !== drawnW) {
      gfx.clear();
      // Zenith cap and nadir skirt extend beyond skyTopY / horizonY so high jumps or deep drops
      // never expose raw black void beyond the gradient backplane.
      gfx.fillRect(0, -SKY_ZENITH_CAP_H, W, SKY_ZENITH_CAP_H, layer.from);
      if (gfx.fillVerticalGradientRect) {
        gfx.fillVerticalGradientRect(0, 0, W, spanH, layer.from, layer.to);
      } else {
        for (let i = 0; i < colors.length; i += 1) {
          const y0 = Math.round(i * strip);
          const h = Math.round((i + 1) * strip) - y0;
          gfx.fillRect(0, y0, W, h, colors[i]);
        }
      }
      gfx.fillRect(0, spanH, W, SKY_NADIR_SKIRT_H, layer.to);
      drawnW = W;
    }
    gfx.setPosition(v.centerX - W / 2, ctx.scene.skyTopY);
  };
  fit();
  const unsub = ctx.addTick(() => fit());

  return makeContentHandle({ onDestroy: () => { unsub(); try { gfx.destroy(); } catch { /* gone */ } } });
};

registerEffect({ kind: 'skyGradient', build: buildSkyGradient, depth: Z_LAYERS.SKY, mooded: false });
